import {
  differenceInCalendarDays,
  differenceInHours,
  formatDistanceToNow,
  isYesterday
} from 'date-fns';
import timezone from 'timezone';
// this should obviously match the TZ value
import DISPLAY_TZ_DATA from 'timezone/America/New_York';

const DISPLAY_TZ = process.env.TZ;
const tz = timezone(DISPLAY_TZ_DATA);

// https://www.gnu.org/software/libc/manual/html_node/Formatting-Calendar-Time.html#index-strftime
const DATE_FORMAT = '%b %-d, %Y';

/**
 * returns an editorial-approved friendly date string for dates in the past when
 * provided a unix epoch in seconds.
 *
 * @export
 * @param {Number} seconds seconds in unix epoch format
 * @returns {String} relative date per our custom logic
 */
export function getRelativeDate(seconds) {
  const thenDate = new Date(seconds * 1000);
  const nowDate = new Date();

  if (isYesterday(thenDate)) {
    return 'Yesterday';
  }

  const daysSince = differenceInCalendarDays(nowDate, thenDate);
  const hoursSince = differenceInHours(nowDate, thenDate);

  if (daysSince < 1 && hoursSince < 1) {
    return 'Now';
  }

  if (daysSince < 4) {
    return formatDistanceToNow(thenDate, {
      includeSeconds: true,
      addSuffix: true
    });
  }

  return tz(thenDate, DATE_FORMAT, DISPLAY_TZ);
}

/**
 * returns an ISO 8601 formatted date in UTC time when given a unix epoch.
 *
 * @export
 * @param {Number} seconds seconds in unix epoch format
 * @returns {String} ISO 8601 formatted date in UTC time
 */
export function getIsoDate(seconds) {
  return new Date(seconds * 1000).toISOString();
}

/**
 * returns a formatted date in NYC timezone when given a unix epoch and a format
 * string in GNU date format.
 *
 * @export
 * @param {Number} seconds seconds in unix epoch format
 * @param {String} formatString  formatted string in NYC timezone
 */
export function getFormattedDate(seconds, formatString) {
  return tz(seconds * 1000, formatString, DISPLAY_TZ);
}

/**
 * naively returns the YYYY-MM-DD part from a provided full ISO 8601 date.
 *
 * @param {String} isoDateStr date in ISO 8601 format
 * @returns {String} YYYY-MM-DD from input
 */
export function getYmdFromIso(isoDateStr) {
  return (isoDateStr && isoDateStr.substring(0, 10)) || '';
}

/**
 * get approx cooktime based on from given seconds
 * @param {String} seconds seconds in unix epoch format
 */
export function getRelativeCookTime(seconds) {
  const hours = Math.floor(seconds / 60 / 60);
  const minutes = Math.floor(seconds / 60);
  if (hours > 0) {
    return `${hours} hr${hours > 1 ? 's' : ''} ${minutes - hours * 60} min`;
  }
  return `${minutes} min`;
}
